import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Tag, TrendingUp, TrendingDown, Zap, ShoppingBag } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { SectionHeader } from './ui/SectionHeader';
import { ModernProductCard } from './ui/ModernProductCard';
import { ModernDealCard } from './ui/ModernDealCard';
import { HeroSearchSection } from './ui/HeroSearchSection';
import { PriceTracker } from './ui/PriceTracker';
import { ProductComparison } from './ui/ProductComparison';
import { publicFeatures } from '../config/publicFeatures';
import { Sidebar } from './ui/Sidebar';
import { MobileFilters } from './ui/MobileFilters';
import { ViewToggle } from './ui/ViewToggle';
import { ProductList } from './ProductList';
import { SEOMetadata } from './SEOMetadata';
import { supabase } from '../lib/supabaseClient';

// ✅ Fetch featured products (default view)
async function fetchFeaturedProducts(filters = {}) {
  let query = supabase.from('products').select('*');

  // Category
  if (filters.category && filters.category !== 'all') {
    query = query.eq('category_slug', filters.category);
  }

  // Search
  if (filters.search) {
    query = query.or(`title.ilike.%${filters.search}%,brand.ilike.%${filters.search}%`);
  }

  // Price range
  if (filters.minPrice) query = query.gte('price', parseFloat(filters.minPrice));
  if (filters.maxPrice) query = query.lte('price', parseFloat(filters.maxPrice));

  // Product tags
  if (filters.productTags && filters.productTags !== 'all') {
    query = query.filter('product_tags', 'cs', JSON.stringify([filters.productTags]));
  }

  // Sort
  switch (filters.sort) {
    case 'price_asc': query = query.order('price', { ascending: true }); break;
    case 'price_desc': query = query.order('price', { ascending: false }); break;
    case 'rating_desc': query = query.order('rating', { ascending: false }); break;
    case 'discount_desc': query = query.order('savings_percentage', { ascending: false }); break;
    default: query = query.order('updated_at', { ascending: false });
  }

  query = query.limit(12);

  const { data, error } = await query;
  if (error) throw new Error(error.message);
  return data || [];
}

// ✅ Today's Hot Deals (only products with "hot_deal" tag, sorted by biggest discount)
async function fetchTodaysDeals() {
  const { data, error } = await supabase
    .from('products')
    .select('*')
    .filter('product_tags', 'cs', JSON.stringify(['hot_deal']))
    .gt('discount', 0)
    .order('discount', { ascending: false })
    .order('updated_at', { ascending: false })
    .limit(8);

  if (error) throw new Error(error.message);
  return data || [];
}

// ✅ Trending Now (high rating or high review count)
async function fetchTrendingProducts() {
  const { data, error } = await supabase
    .from('products')
    .select('*')
    .gte('rating', 4.3) // only well-rated products
    .order('review_count', { ascending: false })
    .limit(8);

  if (error) throw new Error(error.message);
  return data || [];
}

// ✅ Editor's Choice (biggest savings)
async function fetchEditorsChoice() {
  const { data, error } = await supabase
    .from('products')
    .select('*')
    .gt('savings_percentage', 10) // only discounted items
    .order('savings_percentage', { ascending: false })
    .limit(8);

  if (error) throw new Error(error.message);
  return data || [];
}

// ✅ Deals (active only)
async function fetchDeals() {
  const nowIso = new Date().toISOString();
  const { data, error } = await supabase
    .from('deals')
    .select(`
      *,
      products (
        title,
        image_url,
        brand,
        rating,
        review_count
      )
    `)
    .eq('is_active', true)
    .gt('end_time', nowIso)
    .order('start_time', { ascending: false })
    .limit(12);

  if (error) throw new Error(error.message);
  return data || [];
}

// ✅ Price Drops
async function fetchPriceDrops() {
  const nowIso = new Date().toISOString();
  const { data, error } = await supabase
    .from('deals')
    .select(`
      *,
      products (
        title,
        image_url,
        price,
        asin
      )
    `)
    .eq('type', 'price_drop')
    .eq('is_active', true)
    .gt('end_time', nowIso)
    .order('end_time', { ascending: false })
    .limit(7);

  if (error) throw new Error(error.message);
  return data || [];
}

export function ModernHomePage() {
  const [filters, setFilters] = useState({
    search: '',
    category: 'all',
    productType: 'all',
    minPrice: '',
    maxPrice: '',
    stockStatus: '',
    sort: 'featured'
  });

  const [view, setView] = useState('grid');
  const [showProductList, setShowProductList] = useState(false);

  // ✅ React Query setup
  const { data: products = [], isLoading: productsLoading, refetch: refetchProducts } = useQuery({ queryKey: ['featured-products', filters], queryFn: () => fetchFeaturedProducts(filters), });

  const { data: todaysDeals = [], isLoading: todaysDealsLoading } = useQuery({
    queryKey: ['todays-deals-v2'],
    enabled: publicFeatures.dailyDeals,
    queryFn: fetchTodaysDeals,
    staleTime: 5 * 60 * 1000,
  });

  const { data: trendingProducts = [], isLoading: trendingLoading } = useQuery({
    queryKey: ['trending-products'],
    enabled: publicFeatures.trending,
    queryFn: fetchTrendingProducts,
  });

  const { data: editorsChoice = [], isLoading: editorsChoiceLoading } = useQuery({
    queryKey: ['editors-choice'],
    queryFn: fetchEditorsChoice,
  });

  const { data: deals = [], isLoading: dealsLoading } = useQuery({
    queryKey: ['home-deals'],
    enabled: publicFeatures.dailyDeals,
    queryFn: fetchDeals,
  });

  const { data: priceDrops = [], isLoading: priceDropsLoading } = useQuery({
    queryKey: ['price-drops'],
    enabled: publicFeatures.dailyDeals,
    queryFn: fetchPriceDrops,
  });

  const handleSearch = (searchTerm) => {
    const newFilters = { ...filters, search: searchTerm };
    setFilters(newFilters);

    if (searchTerm) {
      setShowProductList(true);
    }

    // Manually trigger product refetch
    refetchProducts();
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    refetchProducts();
  };

  const handleBackToHome = () => {
    setShowProductList(false);
    setFilters({
      search: '',
      category: 'all',
      productType: 'all',
      minPrice: '',
      maxPrice: '',
      stockStatus: '',
      sort: 'featured'
    });
  };

  // Enhanced SEO data for homepage
  const homepageBreadcrumbs = [
    { name: "Home", url: "/" }
  ];

  return (
    <>
      <SEOMetadata 
        title="TechieDay - Best Tech Deals, Price Comparisons & Reviews 2025"
        description="Discover unbeatable tech deals with real-time price tracking. Compare prices, read expert reviews, and find the best deals on gaming gear, electronics, and accessories."
        keywords={["tech deals", "price comparison", "gaming gear", "electronics", "amazon deals", "tech reviews", "best prices"]}
        canonicalUrl="/"
        type="website"
        imageUrl="/images/thumb.jpeg"
        products={products.slice(0, 12)}
        breadcrumbs={homepageBreadcrumbs}
        lastModified={new Date().toISOString()}
      />

      <div className="min-h-screen">
        {!showProductList ? (
          <div className="space-y-12 sm:space-y-16">
            {/* SEO: Main H1 for Homepage */}
            <header className="sr-only">
              <h1>Best Tech Deals & Price Tracking 2025 - TechieDay</h1>
            </header>

            {/* Enhanced Hero Search Section */}
            <HeroSearchSection 
              onSearch={handleSearch}
              onFilterChange={handleFilterChange}
              filters={filters}
            />

            {/* Today's Deals Section with Enhanced Mobile Design */}
            {publicFeatures.dailyDeals && todaysDeals.length > 0 && (
              <motion.section
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                className="today-deals-section relative container mx-auto px-4 sm:px-6 lg:px-8"
              >
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-6">
                  <SectionHeader 
                    icon={Tag} 
                    title="🔥 Today's Hot Deals" 
                    subtitle="Limited time offers - grab them before they're gone!"
                    linkTo="/daily-deals"
                  />

                  {/* Urgency Indicator */}
                  <div className="hidden sm:flex items-center justify-center sm:justify-end gap-2 text-sm text-red-600 dark:text-red-400 font-medium" style={{ minWidth: '100px' }}>
                    <span className="animate-pulse" aria-hidden="true">⏰</span>
                    <span>Ends Soon!</span>
                  </div>
                </div>
                
                {todaysDealsLoading ? (
                  <div className="flex gap-4 pb-4 -mb-4 overflow-x-auto scrollbar-hide">
                    {[...Array(4)].map((_, i) => (
                      <div key={i} className="flex-shrink-0 w-64 sm:w-72 h-80 bg-slate-200 dark:bg-dark-tertiary rounded-lg animate-pulse"></div>
                    ))}
                  </div>
                ) : (
                  <>
                    <div className="flex gap-4 sm:gap-5 overflow-x-auto scrollbar-hide py-4 -mb-4 snap-x snap-mandatory">
                      {todaysDeals.map((product, index) => (
                        <motion.div
                          key={product.asin}
                          initial={{ opacity: 0, x: 50 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.4, delay: index * 0.1 }}
                          viewport={{ once: true }}
                          className="flex-shrink-0 w-64 sm:w-72 md:w-[calc(33.333%-1rem)] lg:w-[calc(25%-0.95rem)] snap-start"
                        >
                          <ModernProductCard product={product} />
                        </motion.div>
                      ))}
                    </div>
                  
                    {/* Mobile CTA */}
                    <div className="flex justify-center mt-6 sm:hidden">
                       <Link
                         to="/daily-deals"
                         className="bg-red-600 hover:bg-red-700 text-white text-sm sm:text-base font-semibold py-3 px-6 rounded-lg transition-colors flex items-center gap-2"
                       >
                         <Tag className="w-4 h-4" aria-hidden="true" />
                         View All Deals
                       </Link>
                    </div>
                  </>
                )}
              </motion.section>
            )}

            {/* Enhanced Price Tracker Section */}
            {publicFeatures.priceAlerts && (
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                className="relative bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-2xl px-4 py-6 sm:px-6 sm:py-8 md:px-8 md:py-10 border border-blue-100 dark:border-blue-800"
              >
                <div className="text-center mb-6">
                  <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-2">
                    📈 Never Miss a Deal Again
                  </h2>
                  <p className="text-sm sm:text-base md:text-lg text-gray-600 dark:text-gray-300 max-w-xl mx-auto">
                    Set price alerts and get notified when your favorite items drop in price
                  </p>
                </div>

                {/* Make sure PriceTracker component is also responsive internally */}
                <div className="w-full">
                  <PriceTracker />
                </div>
              </motion.div>
            </div>


            )}

            {/* Enhanced Product Comparison */}
            {publicFeatures.comparisons && (
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                {/* Make sure PriceTracker component is also responsive internally */}
                <div className="w-full">
                  <ProductComparison />
                </div>
              </motion.div>
            </div>
            
            )}

            {(publicFeatures.trending || publicFeatures.dailyDeals) && (
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
                
                {/* Enhanced Trending Products Section */}
                {publicFeatures.trending && (
                <motion.section
                  initial={{ opacity: 0, x: 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                  viewport={{ once: true }}
                  className="flex-1"
                >
                  <div className="w-full flex flex-col justify-center gap-2 text-center mb-6">
                    <SectionHeader 
                      icon={TrendingUp} 
                      title="🚀 Trending Now" 
                      subtitle="What everyone's buying today"
                      linkTo="/tag/trending"
                    />
                    <div className="flex justify-center gap-4 text-sm text-sm text-green-600 dark:text-green-400 font-medium text-center">
                      <span aria-hidden="true">📊</span>
                      <span className="break-keep">Real time data</span>
                    </div>
                  </div>
                  
                  {productsLoading ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                      {[...Array(4)].map((_, i) => (
                        <div key={i} className="h-80 bg-slate-200 dark:bg-dark-tertiary rounded-lg animate-pulse"></div>
                      ))}
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                      {trendingProducts.map((product, index) => (
                        <motion.div
                          key={product.asin}
                          initial={{ opacity: 0, y: 30 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.4, delay: index * 0.1 }}
                          viewport={{ once: true }}
                        >
                          <ModernProductCard product={product} />
                        </motion.div>
                      ))}
                    </div>
                  )}
                </motion.section>

                )}

                {/* Enhanced Price Drops Section */}
                {publicFeatures.dailyDeals && priceDrops.length > 0 && (
                  <motion.section
                    initial={{ opacity: 0, x: -30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6 }}
                    viewport={{ once: true }}
                    className="flex-1 lg:max-w-md"
                  >
                    <div className="flex items-center justify-between mb-6">
                      <SectionHeader 
                        icon={TrendingDown} 
                        title="⚡ Price Drops" 
                        subtitle="Recently discounted"
                        linkTo="/price-drops-today"
                      />
                      <div className="text-xs text-orange-600 dark:text-orange-400 font-medium">
                        Updated hourly
                      </div>
                    </div>
                    
                    {priceDropsLoading ? (
                      <div className="space-y-4">
                        {[...Array(4)].map((_, i) => (
                          <div key={i} className="h-20 bg-slate-200 dark:bg-dark-tertiary rounded-lg animate-pulse"></div>
                        ))}
                      </div>
                    ) : (
                      <div className="space-y-3 sm:space-y-4">
                        {priceDrops.slice(0, 5).map((deal, index) => (
                          <motion.div
                            key={deal.id}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.4, delay: index * 0.1 }}
                            viewport={{ once: true }}
                          >
                            <Link
                              to={`/dp/${deal.asin}`}
                              className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 bg-white dark:bg-dark-card border border-slate-200 dark:border-dark-primary rounded-lg hover:border-green-500/50 dark:hover:border-green-400/50 transition-all duration-300 group hover:shadow-md"
                            >
                              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gray-100 dark:bg-dark-tertiary rounded-md flex items-center justify-center flex-shrink-0 overflow-hidden">
                                {deal.products?.image_url ? (
                                  <img
                                    src={deal.products.image_url}
                                    alt={`${deal.products.title || 'Product'} - Hot deal with price drop on Amazon`}
                                    className="w-auto h-auto max-w-full max-h-full object-contain"
                                    loading="lazy"
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                      e.target.nextSibling.style.display = 'flex';
                                    }}
                                  />
                                ) : (
                                  <ShoppingBag className="w-4 h-4 sm:w-6 sm:h-6 text-gray-400 dark:text-gray-500" />
                                )}
                                {deal.products?.image_url && (
                                  <ShoppingBag className="w-4 h-4 sm:w-6 sm:h-6 text-gray-400 dark:text-gray-500 hidden" />
                                )}
                              </div>
                              <div className="flex-grow min-w-0">
                                <h3 className="line-clamp-2 font-semibold text-sm sm:text-base text-slate-800 dark:text-slate-100 group-hover:text-green-600 dark:group-hover:text-green-400 transition-colors mb-1">
                                  {deal.products?.title || `Product ${deal.asin}`}
                                </h3>
                                <div className="flex items-baseline gap-2 mb-1">
                                  <p className="text-base sm:text-lg font-bold text-green-600 dark:text-green-400">
                                    ${Number(deal.deal_price).toFixed(2)}
                                  </p>
                                  {deal.original_price && (
                                    <p className="text-xs sm:text-sm text-slate-400 dark:text-slate-500 line-through">
                                      ${Number(deal.original_price).toFixed(2)}
                                    </p>
                                  )}
                                </div>
                                <p className="text-xs text-orange-600 dark:text-orange-400 font-medium">
                                  📉 Price dropped recently
                                </p>
                              </div>
                              <div className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                                <span className="text-xs text-blue-600 dark:text-blue-400 font-medium">
                                  View →
                                </span>
                              </div>
                            </Link>
                          </motion.div>
                        ))}
                      </div>
                    )}
                  </motion.section>
                )}
                
              </div>
            </div>

            )}

            {/* Enhanced Featured Products Grid */}
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">

                {/* Enhanced Trending Products Section */}
                <motion.section
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                  viewport={{ once: true }}
                >
                  <div className="mb-6 ms:text-center">
                    <SectionHeader 
                      icon={ShoppingBag} 
                      title="⭐ Editor's Choice" 
                      subtitle="Handpicked deals and trending items from our experts"
                      linkTo="/tag/best_seller"
                    />

                    <div className="flex flex-wrap justify-center gap-4 text-sm mt-6">
                      <span className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 px-3 py-1 rounded-full">
                        ✅ Price Verified
                      </span>
                      <span className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 px-3 py-1 rounded-full">
                        🚀 Top Rated
                      </span>
                      <span className="bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200 px-3 py-1 rounded-full">
                        💎 Best Value
                      </span>
                    </div>
                  </div>
                  
                  {productsLoading ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                      {[...Array(10)].map((_, i) => (
                        <div key={i} className="h-80 bg-slate-200 dark:bg-dark-tertiary rounded-lg animate-pulse"></div>
                      ))}
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                      {products.slice(0, 10).map((product, index) => (
                        <motion.div
                          key={product.asin}
                          initial={{ opacity: 0, y: 30 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.4, delay: index * 0.1 }}
                          viewport={{ once: true }}
                          className="editor-choice-product-card"
                        >
                          <ModernProductCard product={product} />
                        </motion.div>
                      ))}
                    </div>
                  )}
                  
                  {/* Enhanced CTA Section */}
                  <div className="flex flex-col sm:flex-row justify-center gap-4 p-6 mt-8">
                    {publicFeatures.comparisons && (
                    <Link
                      to="/compare"
                      className="group flex justify-center items-center gap-3 flex-shrink-0 border border-slate-200 dark:border-dark-primary rounded-lg py-3 md:py-4 px-6 bg-white dark:bg-dark-card hover:shadow-lg dark:hover:shadow-2xl text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-all duration-300 hover:-translate-y-1"
                    >
                      <Zap className="w-5 h-5 group-hover:animate-pulse" />
                      <span className="text-xs sm:text-base font-semibold">Compare Products</span>
                    </Link>
                    )}
                    
                    <button
                      onClick={() => setShowProductList(true)}
                      className="line-bg group flex justify-center items-center gap-3 flex-shrink-0 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white text-xs sm:text-base font-semibold py-3 md:py-4 px-6 rounded-lg transition-all duration-300 hover:shadow-lg hover:-translate-y-1"
                    >
                      <ShoppingBag className="w-5 h-5 group-hover:scale-110 transition-transform" />
                      <span>Browse All Products</span>
                    </button>
                  </div>
                </motion.section>
              </div>
            </div>
          </div>
        ) : (
          // Product List View with Sidebar and Controls
          <div className="min-h-screen bg-white dark:bg-dark-card rounded-lg">
            <header className="max-w-7xl mx-auto px-4">
              <div className="bg-white rounded-lg shadow-sm space-y-6 dark:bg-dark-card/80 backdrop-blur-lg border-b border-slate-200 dark:border-dark-primary shadow-sm p-6">
                <div className="flex gap-4 justify-between items-center flex-col md:flex-row md:text-left text-center">
                  <div>
                    <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
                      Search Results
                    </h1>
                    <p className="mt-2 text-slate-500 dark:text-slate-400">
                      {filters.search ? `Results for "${filters.search}"` : 'All Products'}
                    </p>
                  </div>
                  <div className="flex items-center justify-center justify-between md:justify-center w-full md:w-auto gap-4">
                    <Link
                      to="/"
                      onClick={handleBackToHome}
                      className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium transition-colors"
                    >
                      ← Back to Home
                    </Link>
                    <ViewToggle view={view} onViewChange={setView} />
                  </div>
                </div>
              </div>
            </header>

            <main className="max-w-7xl mx-auto px-4 py-8">
              <div className="flex gap-8">
                <Sidebar filters={filters} onFilterChange={setFilters} />
                <div className="w-full flex-grow">
                  <ProductList filters={filters} view={view} />
                </div>
              </div>
            </main>

            <MobileFilters filters={filters} onFilterChange={setFilters} />
          </div>
        )}
      </div>
    </>
  );
}
